import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import apiClient from '../utils/api';
import { AlertDialog, PromptDialog } from './CustomDialog';
import './ProxyCardsWidget.css';

function ProxyCardsWidget() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [cards, setCards] = useState([]);
  const [frequencies, setFrequencies] = useState([]);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Dialog states
  const [alertDialog, setAlertDialog] = useState(null);
  const [promptDialog, setPromptDialog] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [cardsData, freqData, subStatus] = await Promise.all([
        apiClient.getMyProxyCards(),
        apiClient.getFrequencies(),
        apiClient.getSubscriptionStatus(),
      ]);
      setCards(cardsData);
      setFrequencies(freqData);
      setSubscriptionStatus(subStatus);
    } catch (error) {
      console.error('Error loading cards:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClaimCard = async () => {
    // Check subscription status
    if (subscriptionStatus && !subscriptionStatus.is_trial && !subscriptionStatus.is_subscribed) {
      setAlertDialog({
        type: 'warning',
        message: t('proxyCards.trialExpired')
      });
      return;
    }
    
    setPromptDialog({
      title: t('proxyCards.addCardTitle'),
      message: t('proxyCards.enterCode'),
      placeholder: t('proxyCards.codePlaceholder'),
      onConfirm: async (code) => {
        setPromptDialog(null);
        if (!code || code.length !== 6) {
          setAlertDialog({
            type: 'error',
            message: t('proxyCards.codeLength')
          });
          return;
        }

        try {
          await apiClient.claimProxyCard(code);
          await loadData();
          setAlertDialog({
            type: 'success',
            title: t('proxyCards.success'),
            message: t('proxyCards.cardActivated')
          });
        } catch (err) {
          setAlertDialog({
            type: 'error',
            message: err.message || t('proxyCards.cardNotFound')
          });
        }
      },
      onCancel: () => setPromptDialog(null)
    });
  };

  const handleAssignFrequency = async (cardId, frequencyId) => {
    // Check subscription status
    if (subscriptionStatus && !subscriptionStatus.is_trial && !subscriptionStatus.is_subscribed) {
      setAlertDialog({
        type: 'warning',
        message: t('proxyCards.trialExpired')
      });
      return;
    }
    
    try {
      await apiClient.assignCardFrequency(cardId, frequencyId);
      await loadData();
    } catch (err) {
      setAlertDialog({
        type: 'error',
        message: t('proxyCards.assignError')
      });
    }
  };

  const getFrequencyDetails = (frequencyId) => {
    return frequencies.find(f => f.id === frequencyId);
  };

  if (loading) return null;

  return (
    <div className="proxy-cards-widget">
      <div className="widget-header">
        <h2 className="widget-title">🎴 {t('proxyCards.title')}</h2>
        <button onClick={handleClaimCard} className="btn-add-card">
          + {t('proxyCards.addCard')}
        </button>
      </div>

      {subscriptionStatus && !subscriptionStatus.is_trial && !subscriptionStatus.is_subscribed ? (
        <div className="subscription-required-message">
          <div className="lock-icon">🔒</div>
          <h3>{t('common.trial')} {t('proxyCards.trialExpired').includes('abgelaufen') ? 'abgelaufen' : 'expired'}</h3>
          <p>{t('proxyCards.trialExpired')}</p>
          <button onClick={() => navigate('/pricing')} className="btn-upgrade">
            {t('proxyCards.upgradeNow')}
          </button>
        </div>
      ) : (
        <>
          {cards.length === 0 ? (
        <div className="empty-cards">
          <div className="empty-icon">🎴</div>
          <p>{t('proxyCards.noCardsYet')}</p>
          <button onClick={handleClaimCard} className="btn-add-card-large">
            + {t('proxyCards.addFirstCard')}
          </button>
        </div>
      ) : (
        <div className="cards-grid">
          {cards.map((card) => {
            const freq = getFrequencyDetails(card.frequency_id);
            return (
              <div key={card.id} className="proxy-card" style={{
                '--card-glow-color': freq?.color_hex || 'transparent'
              }}>
                <div className="card-background">
                  <div className="card-shine"></div>
                  <div className="card-pattern"></div>
                </div>
                
                <div className="card-content">
                  <div className="card-header">
                    <div className="card-logo">
                      <img
                        src="https://customer-assets.emergentagent.com/job_f48ed865-f137-40ec-8ec5-750617ca0014/artifacts/jm5y5oub_logo.png"
                        alt="ZENTA"
                        className="card-logo-img"
                      />
                      <div className="card-logo-text">ZENTA frequenz</div>
                    </div>
                  </div>

                  <div className="card-code">{card.code}</div>

                  <div className="card-footer">
                    <div className="card-frequency-selector">
                      <select
                        value={card.frequency_id || ''}
                        onChange={(e) => handleAssignFrequency(card.id, e.target.value || null)}
                        className="freq-select"
                      >
                        <option value="">{t('proxyCards.selectFrequency')}</option>
                        {frequencies.map((f) => (
                          <option key={f.id} value={f.id}>
                            {f.icon} {f.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {freq && (
                  <div className="card-glow" style={{ backgroundColor: freq.color_hex }}></div>
                )}
              </div>
            );
          })}
        </div>
      )}
      </>
      )}

      {/* Custom Dialogs */}
      {alertDialog && (
        <AlertDialog
          title={alertDialog.title}
          message={alertDialog.message}
          type={alertDialog.type}
          onClose={() => setAlertDialog(null)}
        />
      )}

      {promptDialog && (
        <PromptDialog
          title={promptDialog.title}
          message={promptDialog.message}
          placeholder={promptDialog.placeholder}
          onConfirm={promptDialog.onConfirm}
          onCancel={promptDialog.onCancel}
          maxLength={6}
        />
      )}
    </div>
  );
}

export default ProxyCardsWidget;
